import { useCallback, useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { BookOpen, Copy, MoonStar, Monitor, Moon, Plug, Sparkles, Sun, Trash2, X } from 'lucide-react'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'
import { useTheme } from '~/lib/theme-context'
import type { DarkVariant, Theme } from '~/lib/theme'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { useCurrentUser, usePermission } from '~/lib/current-user'
import {
  createApiTokenRpc,
  listApiTokensRpc,
  revokeApiTokenRpc,
} from '~/server/tokens'
import { seo } from '~/utils/seo'

const SETTINGS_TABS = ['appearance', 'developer'] as const
type SettingsTab = (typeof SETTINGS_TABS)[number]

export const Route = createFileRoute('/settings')({
  head: () => ({ meta: seo({ title: 'Settings' }) }),
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } => {
    const raw = search.tab
    return SETTINGS_TABS.includes(raw as SettingsTab) ? { tab: raw as SettingsTab } : {}
  },
  component: SettingsPage,
})

interface TokenRow {
  id: string
  name: string
  prefix: string
  createdAt: string
  lastUsedAt: string | null
}

function SettingsPage() {
  const { user } = useCurrentUser()
  const { tab = 'appearance' } = Route.useSearch()
  const navigate = useNavigate()

  if (!user) {
    return <div className="text-muted-foreground text-sm">Loading...</div>
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title="Settings" description="Manage appearance and API access" />

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({ to: '/settings', search: { tab: value as SettingsTab }, replace: true })
        }
      >
        <TabsList>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="developer">Developer Options</TabsTrigger>
        </TabsList>

        <TabsContent value="appearance" className="pt-4">
          <AppearanceSection />
        </TabsContent>

        <TabsContent value="developer" className="pt-4 space-y-6">
          <TokensSection userId={user.id} />
          <DeveloperLinksSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun; description: string }[] = [
  { value: 'light', label: 'Light', icon: Sun, description: 'Crisp white light theme.' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Deep navy dark theme.' },
  { value: 'graphite', label: 'Graphite', icon: MoonStar, description: 'Neutral charcoal — easier on the eyes than navy.' },
  { value: 'tokyo', label: 'Tokyo Night', icon: Sparkles, description: 'Purple-blue dark theme inspired by the Tokyo Night palette.' },
  { value: 'system', label: 'System', icon: Monitor, description: 'Follow your operating system preference.' },
]

const DARK_VARIANT_OPTIONS: { value: DarkVariant; label: string }[] = [
  { value: 'dark', label: 'Dark' },
  { value: 'graphite', label: 'Graphite' },
  { value: 'tokyo', label: 'Tokyo Night' },
]

function AppearanceSection() {
  const { theme, setTheme, systemDark, setSystemDark } = useTheme()

  return (
    <div className="space-y-4 rounded-md border bg-card p-6">
      <div>
        <h2 className="text-lg font-semibold">Theme</h2>
        <p className="text-sm text-muted-foreground">Choose how the app looks to you.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {THEME_OPTIONS.map(({ value, label, icon: Icon, description }) => {
          const active = theme === value
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              aria-pressed={active}
              className={cn(
                'flex flex-col gap-2 rounded-md border bg-background p-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground',
                active && 'border-primary ring-2 ring-primary/40',
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className="size-4" />
                <span className="font-medium">{label}</span>
              </div>
              <span className="text-xs text-muted-foreground">{description}</span>
            </button>
          )
        })}
      </div>
      {theme === 'system' && (
        <div className="space-y-3 rounded-md border bg-muted/40 p-4">
          <div>
            <div className="text-sm font-medium">When system is dark, use</div>
            <p className="text-xs text-muted-foreground">
              Picks which dark palette to apply whenever your OS reports dark mode.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {DARK_VARIANT_OPTIONS.map(({ value, label }) => {
              const active = systemDark === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSystemDark(value)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-md border bg-background px-3 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                    active && 'border-primary ring-2 ring-primary/40',
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function DeveloperLinksSection() {
  const canApi = usePermission('api:read')
  const canMcp = usePermission('mcp:read')
  if (!canApi && !canMcp) return null
  return (
    <div className="space-y-4 rounded-md border bg-card p-6">
      <div>
        <h2 className="text-lg font-semibold">Developer References</h2>
        <p className="text-sm text-muted-foreground">Reference material for building against the API.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {canApi && (
          <Link
            to="/openapi"
            className="flex items-start gap-3 rounded-md border bg-background p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <BookOpen className="size-4 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-medium">API Reference</div>
              <p className="text-xs text-muted-foreground">Browse the OpenAPI schema for every REST endpoint.</p>
            </div>
          </Link>
        )}
        {canMcp && (
          <Link
            to="/mcp-instructions"
            className="flex items-start gap-3 rounded-md border bg-background p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Plug className="size-4 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-medium">MCP Instructions</div>
              <p className="text-xs text-muted-foreground">Connect AI clients via the Model Context Protocol.</p>
            </div>
          </Link>
        )}
      </div>
    </div>
  )
}

function TokensSection({ userId }: Readonly<{ userId: string }>) {
  const [tokens, setTokens] = useState<TokenRow[] | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newTokenName, setNewTokenName] = useState('')
  const [mintedPlaintext, setMintedPlaintext] = useState<string | null>(null)
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      const rows = await listApiTokensRpc()
      setTokens(rows)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [userId, load])

  async function handleCreate() {
    if (!newTokenName.trim()) return
    setCreating(true)
    try {
      const result = await createApiTokenRpc({ data: { name: newTokenName.trim() } })
      setMintedPlaintext(result.plaintext)
      setNewTokenName('')
      await load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(tokenId: string) {
    setPendingRevokeId(tokenId)
    try {
      await revokeApiTokenRpc({ data: { tokenId } })
      await load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPendingRevokeId(null)
    }
  }

  return (
    <div className="space-y-4 rounded-md border bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Personal Access Tokens</h2>
          <p className="text-sm text-muted-foreground">
            Tokens authenticate as you when calling the REST API. Pass them in the{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">x-api-token</code> header.
          </p>
        </div>
        <Button
          onClick={() => {
            setMintedPlaintext(null)
            setCreateOpen(true)
          }}
        >
          Create token
        </Button>
      </div>

      {tokens === null && <div className="text-muted-foreground text-sm">Loading tokens...</div>}
      {tokens !== null && tokens.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No active tokens.</p>
      )}
      {tokens !== null && tokens.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Prefix</th>
              <th className="py-2 font-medium">Created</th>
              <th className="py-2 font-medium">Last used</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-t">
                <td className="py-2">{t.name}</td>
                <td className="py-2 font-mono text-xs">{t.prefix}…</td>
                <td className="py-2 text-muted-foreground">{new Date(t.createdAt).toLocaleDateString()}</td>
                <td className="py-2 text-muted-foreground">
                  {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevoke(t.id)}
                    disabled={pendingRevokeId === t.id}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{mintedPlaintext ? 'Copy your new token' : 'Create a new token'}</DialogTitle>
          </DialogHeader>

          {mintedPlaintext ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This token will only be shown once. Store it somewhere safe — you won't be able to see it again.
              </p>
              <div className="flex items-center gap-2 rounded-md border bg-muted p-3">
                <code className="flex-1 break-all font-mono text-xs">{mintedPlaintext}</code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(mintedPlaintext)}>
                  <Copy className="size-3.5" />
                </Button>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setMintedPlaintext(null)
                    setCreateOpen(false)
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="tokenName">Name</Label>
                <Input
                  id="tokenName"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder="ci-bot"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">A short label to remember what this token is for.</p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                  <X className="size-3.5" />
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={!newTokenName.trim() || creating}>
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  } catch {
    toast.error('Failed to copy — select and copy manually')
  }
}
